
import React, { useEffect, useReducer, useState } from 'react';

import { Api } from '../utilities';
import { PromotionContext } from '../context';
import { usePosthog } from './PosthogProvider';
import { useFrami } from '../context';

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_PROMOTIONS': {
            return { 
                ...state,
                promotions: action.payload || []
            };
        }
        case 'SET_ACTIVE_PROMOTION': {
            return { ...state, promotion: action.payload };
        }
        default:
            return state;
    }
};

export const PromotionProvider = (props) => {
    const [state, dispatch] = useReducer(reducer, { promotions: [], promotion: null });
    const [isPromotionActive, setIsPromotionActive] = useState(false);
    const posthog = usePosthog();
    const { currentFrami } = useFrami();

    const variant = posthog.getFeatureFlag('discount-60-vs-40') === 'test';
    
    // Function to select promotion based on A/B test
    const selectPromotionBasedOnABTest = (promotions) => {
        if (!promotions || promotions.length === 0) return null;
        
        // If only one promotion, return it
        if (promotions.length === 1) {
            return promotions[0];
        }
        
        // Backend returns promotions sorted by discount (lowest first)
        // 'test' variant gets the higher discount (second promotion)
        // 'control' variant gets the lower discount (first promotion)
        return variant && promotions.length > 1 ? promotions[1] : promotions[0];
    };

    useEffect(() => {
        const getActivePromotions = async () => {
            try {
                const currency = currentFrami?.currency || 'EUR';
                const res = await Api.get(`/orders/promotions/?currency=${currency}`);
                const promotions = Array.isArray(res) ? res : (res.coupon ? [res] : []);
                
                // Set promotions first
                dispatch({ type: 'SET_PROMOTIONS', payload: promotions });
                
                // Then select and set active promotion based on A/B test
                const selectedPromotion = selectPromotionBasedOnABTest(promotions);
                dispatch({ type: 'SET_ACTIVE_PROMOTION', payload: selectedPromotion });
                
            } catch (error) {
                console.error("Error fetching promotions:", error);
                dispatch({ type: 'SET_PROMOTIONS', payload: [] });
                dispatch({ type: 'SET_ACTIVE_PROMOTION', payload: null });
            }
        };

        // Only fetch promotions when we have currentFrami (and currency)
        if (currentFrami) {
            getActivePromotions();
        }
    }, [currentFrami?.currency]); // Re-run when currency changes

    // Re-select promotion when PostHog feature flag changes
    useEffect(() => {
        if (state.promotions.length > 0) {
            const selectedPromotion = selectPromotionBasedOnABTest(state.promotions);
            dispatch({ type: 'SET_ACTIVE_PROMOTION', payload: selectedPromotion });
        }
    }, [variant, state.promotions]);

    useEffect(() => {
        if (state.promotion) {
            const now = new Date();
            setIsPromotionActive(new Date(state.promotion.end_date) > now);
        } else {
            setIsPromotionActive(false);
        }
    }, [state.promotion]);

    const hidePromo = () => {
        setIsPromotionActive(false);
    };

    const value = {
        promotion: state.promotion,
        promotions: state.promotions,
        isPromotionActive,
        setIsPromotionActive,
        hidePromo,
    };

    return <PromotionContext.Provider value={value} {...props} />;
};
